import { type FC, useMemo } from "react";
import { Button, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormBuilder } from "@/shared/components/Form";
import { DialogHeader } from "@/shared/components";
import { getPostFormSchema, type PostFormValues } from "../model";

const FORM_ID = "post-form";

interface PostFormProps {
  title: string;
  loading?: boolean;
  initialValues?: PostFormValues;
  onSubmit: (values: PostFormValues) => Promise<void>;
  onClose: () => void;
  metadata?: Record<string, unknown>;
}

export const PostForm: FC<PostFormProps> = ({
  title,
  loading = false,
  initialValues = {},
  onSubmit,
  onClose,
  metadata,
}) => {
  const { t } = useTranslation();

  const schema = useMemo(() => getPostFormSchema(t), [t]);

  const isDraft = !initialValues.publishAt;
  let publish = !isDraft;

  const handleSubmit = async (values: Record<string, unknown>) => {
    await onSubmit({
      ...(values as unknown as PostFormValues),
      slug: (values.slug as string) || null,
      publishAt: publish ? new Date().toISOString() : null,
    });
  };

  return (
    <>
      <DialogHeader
        title={title}
        actions={[
          <Button
            variant="contained"
            type="submit"
            form={FORM_ID}
            disabled={loading}
            onClick={() => (publish = true)}
          >
            {isDraft ? t("common.actions.publish") : t("common.actions.save")}
          </Button>,
          <Button
            variant="outlined"
            type="submit"
            form={FORM_ID}
            disabled={loading}
            onClick={() => (publish = false)}
          >
            {isDraft ? t("common.actions.save") : t("common.actions.hide")}
          </Button>,
        ]}
        loading={loading}
        onClose={onClose}
      />

      <DialogContent sx={{ p: 1 }}>
        <FormBuilder
          formId={FORM_ID}
          schema={schema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          submitButton={false}
          resetButton={false}
          disabled={loading}
          metadata={metadata}
        />
      </DialogContent>
    </>
  );
};
